import {
  TirageHistory,
  TirageHistoryMergedAuction,
  useCreateTirageHistory,
  useDeleteTirageHistory,
  useEditTirageHistory,
  useGetHistoryOfTirage,
  useGetHistoryOfTirageMerged,
  useReorderTirageProvenance,
  useUpdateTirageHistorySequence,
} from "@/api/tirages/tirage-history.api";
import * as AF from "@/components/form-items/AbstractForm";
import {
  MaskableTableSection,
  MaskableTableSectionBody,
  MaskableTableSectionHeader,
} from "@/components/form-items/MaskableTableSection";
import { BooleanBadge } from "@/components/ui/boolean-badge";
import {
  AddButton,
  CancelButton,
  DeleteButton,
  SaveButton,
  UpdateButton,
} from "@/components/ui/custom-buttons";
import {
  CustomDialogContent,
  CustomDialogFooter,
  CustomDialogTitle,
} from "@/components/ui/custom-dialog";
import { DataTable } from "@/components/ui/datatable";
import { Dialog, DialogClose, DialogHeader } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { RegisterVisibilityField } from "@/context/FormFieldVisibilityContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = z.object({
  tirageHistoriqueHistoriqueNom: z.string(),
  tirageHistoriqueHistoriqueVille: z.string(),
  tirageHistoriqueHistoriqueDates: z.string(),
  tirageHistoriqueHistoriqueCollectionParticuliere: z.boolean(),
});

type TirageHistoryForm = z.infer<typeof formSchema>;

const HISTORY_TABLE_MASK_GROUP = "tirageHistoriqueTableGroup";

/** Dialog inputs unmount when closed — keep them in the Champs drawer. */
const HISTORY_DIALOG_FIELDS = [
  "tirageHistoriqueHistoriqueNom",
  "tirageHistoriqueHistoriqueVille",
  "tirageHistoriqueHistoriqueDates",
] as const;

type CreateOrEditTirageHistoryProps = {
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  open: boolean;
  tirageHistory?: TirageHistory;
  tirageId: string;
};

export const CreateOrEditTirageHistory = ({
  disabled = false,
  onOpenChange,
  open,
  tirageHistory,
  tirageId,
}: CreateOrEditTirageHistoryProps) => {
  const isEdit = tirageHistory !== undefined;

  const updater = useEditTirageHistory();
  const creator = useCreateTirageHistory();
  const deleter = useDeleteTirageHistory();

  const { t } = useTranslation();

  const form = useForm<TirageHistoryForm>({
    defaultValues: isEdit
      ? tirageHistory
      : {
          tirageHistoriqueHistoriqueCollectionParticuliere: false,
          tirageHistoriqueHistoriqueDates: "",
          tirageHistoriqueHistoriqueNom: "",
          tirageHistoriqueHistoriqueVille: "",
        },
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (tirageHistory) {
      form.reset(tirageHistory);
    }
  }, [tirageHistory, form]);

  function onSubmit(values: TirageHistoryForm) {
    if (isEdit && tirageHistory) {
      updater.mutate(
        {
          historyId: tirageHistory.id,
          tirageId: tirageHistory.tirageId,
          history: values,
        },
        { onSuccess: () => onOpenChange?.(false) },
      );
      return;
    }

    creator.mutate(
      { history: values, tirageId },
      {
        onSuccess: () => {
          form.reset();
          onOpenChange?.(false);
        },
      },
    );
  }

  function onDelete() {
    if (!isEdit || !tirageHistory) return;
    deleter.mutate(
      { tirageId: tirageHistory.tirageId, historyId: tirageHistory.id },
      {
        onSuccess: () => onOpenChange?.(false),
      },
    );
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <CustomDialogContent>
        <Form {...form}>
          <form
            className="flex flex-col"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <DialogHeader>
              <CustomDialogTitle>{t("addArtworkHistory")}</CustomDialogTitle>
            </DialogHeader>
            <AF.Col className="p-6">
              <AF.Text name="tirageHistoriqueHistoriqueNom" />
              <AF.Text name="tirageHistoriqueHistoriqueVille" />
              <AF.Text name="tirageHistoriqueHistoriqueDates" />
              <AF.Checkbox name="tirageHistoriqueHistoriqueCollectionParticuliere" />
              {isEdit && (
                <DeleteButton
                  variant={"link"}
                  className="text-red-600 w-fit m-auto"
                  iconProps={{ className: "stroke-red-600" }}
                  onClick={onDelete}
                  disabled={disabled}
                >
                  {t("supprimerOeuvresLocalisation")}
                </DeleteButton>
              )}
            </AF.Col>
            <CustomDialogFooter>
              <DialogClose asChild>
                <CancelButton className="flex-1" />
              </DialogClose>
              {isEdit ? (
                <UpdateButton type="submit" className="flex-1" disabled={disabled} />
              ) : (
                <SaveButton type="submit" className="flex-1" disabled={disabled} />
              )}
            </CustomDialogFooter>
          </form>
        </Form>
      </CustomDialogContent>
    </Dialog>
  );
};

type HistoryRow =
  | (TirageHistory & { kind: "history" })
  | (TirageHistoryMergedAuction & { kind: "auction" });

const isHistoryRow = (
  row: HistoryRow,
): row is TirageHistory & { kind: "history" } => row.kind === "history";

export const TirageHistoryTable = ({
  tirageId,
  disabled = false,
  provenanceMode = false,
}: {
  tirageId?: string;
  disabled?: boolean;
  provenanceMode?: boolean;
}) => {
  const { t } = useTranslation();

  const { data: historyOnly = [] } = useGetHistoryOfTirage(
    provenanceMode ? undefined : tirageId,
  );
  const { data: merged } = useGetHistoryOfTirageMerged(tirageId, provenanceMode);

  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [openCreateForm, setOpenCreateForm] = useState(false);

  const historyToUpdate = useRef<TirageHistory>();

  const sequenceMutator = useUpdateTirageHistorySequence();
  const provenanceReorderMutator = useReorderTirageProvenance();

  useEffect(() => {
    if (provenanceMode && merged) {
      setRows([...merged.timeline]);
      return;
    }
    setRows(
      historyOnly.map<HistoryRow>((h) => ({ ...h, kind: "history" as const })),
    );
  }, [historyOnly, merged, provenanceMode]);

  function handleUpdate(tirageHistory: TirageHistory) {
    historyToUpdate.current = tirageHistory;
    setOpenEditForm(true);
  }

  const datatableColumns = useMemo(
    (): ColumnDef<HistoryRow>[] => [
      {
        size: 1000,
        header: t("tab_tirageHistoriqueHistoriqueNom"),
        cell: ({ row }) => {
          const r = row.original;
          if (isHistoryRow(r)) return r.tirageHistoriqueHistoriqueNom;
          const heading = [
            r.auction?.ventesDescriptionCommissaires,
            r.auction?.ventesDescriptionLieu,
          ]
            .filter(Boolean)
            .join(", ");
          const parts = [
            heading,
            r.tirageDansVentesDescriptionNumeroLot
              ? `Lot n°${r.tirageDansVentesDescriptionNumeroLot}`
              : null,
          ].filter(Boolean);
          return parts.length ? parts.join(" - ") : t("sculpture.history.auction");
        },
      },
      {
        header: t("tab_tirageHistoriqueHistoriqueVille"),
        cell: ({ row }) => {
          const r = row.original;
          if (isHistoryRow(r)) return r.tirageHistoriqueHistoriqueVille;
          return [r.auction?.ventesDescriptionVille, r.auction?.ventesDescriptionPays]
            .filter(Boolean)
            .join(", ");
        },
      },
      {
        header: t("tab_tirageHistoriqueHistoriqueDates"),
        cell: ({ row }) => {
          const r = row.original;
          if (isHistoryRow(r)) return r.tirageHistoriqueHistoriqueDates;
          return r.auction?.ventesDescriptionDate ?? "";
        },
      },
      {
        header: t("tab_tirageHistoriqueHistoriqueCollectionParticuliere"),
        cell: ({ row }) => {
          const r = row.original;
          if (!isHistoryRow(r)) return null;
          return (
            <BooleanBadge
              value={r.tirageHistoriqueHistoriqueCollectionParticuliere}
            />
          );
        },
      },
    ],
    [t],
  );

  const handleRowDragged = (newData: HistoryRow[]) => {
    if (!tirageId) return;
    setRows(newData);
    if (provenanceMode) {
      provenanceReorderMutator.mutate({
        tirageId,
        timeline: newData.map((r) => ({ kind: r.kind, id: r.id })),
      });
      return;
    }
    sequenceMutator.mutate({
      newSequence: newData.filter(isHistoryRow).map((d) => d.id),
      tirageId,
    });
  };

  const titleLabel = t(
    provenanceMode ? "tiragePrintProvenance" : "tiragePrintHistory",
  );

  const addButton = (
    <AddButton
      type="button"
      onClick={() => setOpenCreateForm(true)}
      disabled={disabled}
    />
  );

  if (!tirageId) {
    return null;
  }

  return (
    <>
      {HISTORY_DIALOG_FIELDS.map((name) => (
        <RegisterVisibilityField key={name} name={name} label={t(name)} />
      ))}
      <MaskableTableSection>
        <CreateOrEditTirageHistory
          tirageId={tirageId}
          tirageHistory={historyToUpdate.current}
          open={openEditForm}
          onOpenChange={setOpenEditForm}
          disabled={disabled}
        />
        <CreateOrEditTirageHistory
          open={openCreateForm}
          onOpenChange={setOpenCreateForm}
          tirageId={tirageId}
          disabled={disabled}
        />
        <MaskableTableSectionHeader
          groupName={HISTORY_TABLE_MASK_GROUP}
          label={titleLabel}
          trailing={addButton}
        />
        <MaskableTableSectionBody groupName={HISTORY_TABLE_MASK_GROUP}>
          <DataTable
            enableDragRows
            onRowDragged={(d) => handleRowDragged(d.newData)}
            columns={datatableColumns}
            data={rows}
            onRowClick={(e) => {
              if (isHistoryRow(e.original)) handleUpdate(e.original);
            }}
          />
        </MaskableTableSectionBody>
      </MaskableTableSection>
    </>
  );
};
